import { plainTextToHtml } from '@pulsify/shared/html'
import { AiTextButton } from '~/features/ai-studio/components'
import { applyGeneratedText } from '~/features/ai-studio/lib/apply-generated-text'
import { TagSelector } from '~/features/shared/components/tag-selector'
import * as m from '~/paraglide/messages'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { createEventSchema } from '~/shared/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { FormInput, FormRichText } from '~/shared/ui/form'
import { EventDescriptionLanguages } from './event-description-languages'

interface EventDetailsSectionProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API
  form: any
}

export function EventDetailsSection({ form }: EventDetailsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.eventDetails()}</CardTitle>
        <CardDescription>{m.basicInformationAboutYourEvent()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form.Field name="title" validators={createFieldValidators(createEventSchema.shape.title)}>
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <FormInput field={field} label={m.eventTitle()} placeholder={m.summerMusicFestival()} />
          )}
        </form.Field>

        <form.Field
          name="description"
          validators={createFieldValidators(createEventSchema.shape.description)}
        >
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <FormRichText
              field={field}
              label={m.description()}
              placeholder={m.describeYourEventInDetail()}
              labelAction={
                <AiTextButton
                  kind="event-description"
                  onUse={(text) => applyGeneratedText(field, plainTextToHtml(text))}
                />
              }
            />
          )}
        </form.Field>

        <EventDescriptionLanguages form={form} />

        <form.Field
          name="tags"
          validators={{
            onChange: ({
              value,
            }: {
              value: { mainCategoryId?: string; subCategoryTags?: string[] }
            }) => {
              if (!value?.mainCategoryId) {
                return m.pleaseSelectACategory()
              }
              if (!value?.subCategoryTags || value.subCategoryTags.length === 0) {
                return m.pleaseSelectAtLeastOneSubcategory()
              }
              if (value.subCategoryTags.length > 3) {
                return m.maximum3SubcategoriesAllowed()
              }
              return undefined
            },
          }}
        >
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <div className="space-y-2" data-field="tags">
              <TagSelector
                selectedCategoryId={field.state.value?.mainCategoryId}
                selectedSubCategoryIds={field.state.value?.subCategoryTags || []}
                onCategoryChange={(categoryId) => {
                  field.handleBlur()
                  field.handleChange({
                    ...field.state.value,
                    mainCategoryId: categoryId,
                    subCategoryTags: [],
                  })
                }}
                onSubCategoriesChange={(subCategoryIds) => {
                  field.handleBlur()
                  field.handleChange({
                    ...field.state.value,
                    subCategoryTags: subCategoryIds,
                  })
                }}
                maxSubCategories={3}
              />
              {field.state.meta.isTouched &&
                field.state.meta.errors &&
                field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
                )}
            </div>
          )}
        </form.Field>
      </CardContent>
    </Card>
  )
}
